import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, MapPin, Gauge, Clock, Fuel, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { apiClient } from '@/lib/api';
import { VehiclePicker } from '@/components/common/VehiclePicker';

/**
 * One vehicle's working condition — what the tracker and the fuel cards know.
 *
 * Read only. Everything on this page is measured: the odometer and engine hours
 * come from the tracker, the position from its last fix, the day's running from
 * the fixes themselves, and the fuel from what the cards paid for.
 *
 * What is deliberately NOT here is a live tank gauge. These trackers report no
 * fuel level, so a dial would be a picture of nothing. Fuel is stated as litres
 * purchased, and labelled as such, because that is the number that exists.
 */

interface InspectionRow {
  id: string; code: string; kind: 'periodic' | 'extraordinary';
  inspected_at: string; result: string; changed_anything: boolean;
  what_changed: string | null; odometer_km: string | null; notes: string | null;
  items: { key: string; label?: string; state: string }[] | null;
  inspected_by_name: string | null;
}

interface Condition {
  vehicle: Record<string, unknown>;
  tracker: Record<string, unknown> | null;
  fuel: { liters: string; sar: string; fills: number; last_at: string | null; source: string };
  today: { firstMove: string | null; lastMove: string | null; hours: { hour: number; fixes: number; moving: number }[] };
  daily: { day: string; km: number }[];
}

const n0 = (v: unknown) => (v == null ? '—' : Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 }));
const clock = (s: string | null) =>
  (s ? new Date(s).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Riyadh' }) : '—');
const stamp = (s: string | null | undefined) =>
  (s ? new Date(s).toLocaleString('en-GB', { timeZone: 'Asia/Riyadh', hour12: false }).slice(0, 17) : '—');

export default function VehicleConditionPage() {
  const [vehicleId, setVehicleId] = useState('');
  // ?v=<id> so a row elsewhere can open straight onto this truck. The picker
  // still works; this just saves finding it twice.
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const v = params.get('v');
    if (v && v !== vehicleId) setVehicleId(v);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const { data, isLoading } = useQuery({
    queryKey: ['vehicle-condition', vehicleId],
    enabled: !!vehicleId,
    queryFn: async () => (await apiClient.get<Condition>(`/api/v1/vehicle-condition/${vehicleId}`)).data,
  });

  /** Every inspection this truck has had — the full report, not a summary. */
  const { data: history } = useQuery({
    queryKey: ['vehicle-inspections', vehicleId],
    enabled: !!vehicleId,
    queryFn: async () => (await apiClient.get<{ items: InspectionRow[] }>(
      `/api/v1/inspections/vehicle/${vehicleId}`)).data.items,
  });
  const [openRow, setOpenRow] = useState<string | null>(null);

  const v = data?.vehicle ?? {};
  const t = data?.tracker ?? null;

  /** Hours of today the truck was moving, 00–23. */
  const movingHours = new Set((data?.today.hours ?? []).filter((h) => h.moving > 0).map((h) => h.hour));
  const workedHours = movingHours.size;
  const maxKm = Math.max(1, ...(data?.daily ?? []).map((d) => d.km));

  const Stat = ({ icon: Icon, value, unit, label, tone = '' }: {
    icon: typeof Gauge; value: string; unit?: string; label: string; tone?: string;
  }) => (
    <div className="rounded-xl border border-border bg-card px-4 py-3">
      <div className="flex items-baseline gap-1.5">
        <span className={`text-2xl font-mono ${tone}`}>{value}</span>
        {unit && <span className="text-xs text-muted-foreground">{unit}</span>}
      </div>
      <div className="text-xs text-muted-foreground inline-flex items-center gap-1.5 mt-0.5">
        <Icon className="h-3 w-3" /> {label}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen w-full bg-background flex flex-col">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="font-display text-lg whitespace-nowrap">Vehicle condition</div>
          <div className="flex-1 max-w-md">
            <VehiclePicker value={vehicleId || null}
              onChange={(id) => { setVehicleId(id ?? ''); setParams(id ? { v: id } : {}); }}
              placeholder="Search a plate…" />
          </div>
          <Link to="/dashboard">
            <Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Dashboard</Button>
          </Link>
        </div>
      </header>

      <main className="flex-1 max-w-[1400px] mx-auto w-full px-6 py-6 space-y-5">
        {!vehicleId && (
          <div className="rounded-xl border border-dashed border-border p-10 text-center text-muted-foreground">
            Pick a vehicle to see its condition.
          </div>
        )}
        {vehicleId && isLoading && (
          <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        )}

        {data && (
          <>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-mono text-xl">{String(v.plate_en ?? v.code ?? '')}</span>
              <span className="text-sm text-muted-foreground">{String(v.brand ?? '')} {String(v.model ?? '')}</span>
              {v.side ? <span className="text-xs rounded-full border border-border px-2 py-0.5">{String(v.side)}</span> : null}
              {v.driver_name ? (
                <span className="text-sm text-muted-foreground">
                  driver: <b className="text-foreground">{String(v.driver_name)}</b>
                  {v.iqama_number ? ` · ${String(v.iqama_number)}` : ''}
                </span>
              ) : null}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat icon={Gauge} value={n0(t?.odometer_km)} unit="KM" label="Total mileage" />
              <Stat icon={Clock} value={n0(t?.engine_hours)} unit="h" label="Engine hours" />
              <Stat icon={Fuel} value={n0(data.fuel.liters)} unit="L" label="Fuel purchased" />
              <Stat icon={Gauge} value={n0(t?.km_today)} unit="KM" label="Distance today" />
            </div>

            {/* Where it is. Coordinates and the region the tracker reports - no
                street address, because nothing here does reverse geocoding and a
                made-up address is worse than none. */}
            <section className="rounded-xl border border-border bg-card p-4 space-y-2">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Position</div>
              <div className="flex items-center gap-2 text-sm">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                {t?.lat != null && t?.lng != null ? (
                  <a className="font-mono underline" target="_blank" rel="noreferrer"
                     href={`https://maps.google.com/?q=${String(t.lat)},${String(t.lng)}`}>
                    {Number(t.lat).toFixed(5)}, {Number(t.lng).toFixed(5)}
                  </a>
                ) : <span className="text-muted-foreground">no position reported</span>}
                {t?.region ? <span className="text-muted-foreground">· {String(t.region).toUpperCase()}</span> : null}
                {t?.status ? <span className="text-muted-foreground">· {String(t.status)}</span> : null}
              </div>
              <div className="text-xs text-muted-foreground">
                Last reported: {stamp(t?.reported_at as string | undefined)} (GMT+3)
              </div>
            </section>

            {/* Today's running, hour by hour. */}
            <section className="rounded-xl border border-border bg-card p-4 space-y-3">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Today’s running</div>
              <div className="flex gap-8 flex-wrap">
                <div><div className="text-2xl font-mono">{clock(data.today.firstMove)}</div><div className="text-xs text-muted-foreground">First movement</div></div>
                <div><div className="text-2xl font-mono">{workedHours}</div><div className="text-xs text-muted-foreground">Hours with movement</div></div>
                <div><div className="text-2xl font-mono">{clock(data.today.lastMove)}</div><div className="text-xs text-muted-foreground">Last movement</div></div>
              </div>
              {/* 24 blocks, one an hour. Green where the tracker saw it move. */}
              <div className="flex gap-[2px]">
                {Array.from({ length: 24 }, (_, h) => (
                  <div key={h} title={`${String(h).padStart(2, '0')}:00`}
                    className={`h-7 flex-1 rounded-[2px] ${movingHours.has(h) ? 'bg-emerald-500' : 'bg-muted'}`} />
                ))}
              </div>
              <div className="flex justify-between text-[10px] text-muted-foreground font-mono">
                {['00', '04', '08', '12', '16', '20', '24'].map((x) => <span key={x}>{x}</span>)}
              </div>
              <p className="text-[11px] text-muted-foreground">
                Movement, not ignition — the tracker reports ignition only as it is now, so it cannot describe a day already past.
              </p>
            </section>

            {/* Distance per day, from the odometer at each day's edges. */}
            <section className="rounded-xl border border-border bg-card p-4 space-y-3">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Distance, last 14 days</div>
              {data.daily.length === 0 ? (
                <div className="text-sm text-muted-foreground">No odometer history in this window.</div>
              ) : (
                <div className="flex items-end gap-1 h-32">
                  {data.daily.map((d) => (
                    <div key={d.day} className="flex-1 flex flex-col items-center gap-1" title={`${d.day} — ${n0(d.km)} km`}>
                      <div className="w-full rounded-t bg-primary/70" style={{ height: `${Math.round((d.km / maxKm) * 100)}%` }} />
                      <span className="text-[9px] text-muted-foreground font-mono">{d.day.slice(5)}</span>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* The inspection record in full. Each row opens to the sheet as it
                was ticked on the day, so a report can be read back rather than
                summarised away. */}
            <section className="rounded-xl border border-border bg-card overflow-hidden">
              <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                <span className="text-xs uppercase tracking-wider text-muted-foreground">Inspection history</span>
                <span className="text-xs text-muted-foreground">
                  {history?.length ?? 0} record{(history?.length ?? 0) === 1 ? '' : 's'}
                </span>
              </div>
              {(history?.length ?? 0) === 0 ? (
                <div className="px-4 py-6 text-sm text-muted-foreground text-center">
                  No inspection has been recorded for this vehicle yet.
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {history!.map((h) => {
                    const open = openRow === h.id;
                    const bad = (h.items ?? []).filter((i) => i.state === 'fail' || i.state === 'attention');
                    return (
                      <div key={h.id}>
                        <button type="button" onClick={() => setOpenRow(open ? null : h.id)}
                          className="w-full text-start px-4 py-3 hover:bg-muted/30 flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-mono text-xs text-muted-foreground">{h.code}</span>
                              <span className={h.kind === 'extraordinary'
                                ? 'text-amber-600 dark:text-amber-400' : ''}>
                                {h.kind === 'extraordinary' ? 'Extraordinary' : 'Periodic'}
                              </span>
                              <span className={h.result === 'fail'
                                ? 'text-rose-600 dark:text-rose-400' : 'text-muted-foreground'}>
                                {h.result.replace(/_/g, ' ')}
                              </span>
                              {h.changed_anything && (
                                <span className="text-[10px] rounded px-1.5 py-0.5 bg-rose-500/15 text-rose-600 dark:text-rose-400">
                                  changed
                                </span>
                              )}
                              {bad.length > 0 && (
                                <span className="text-[10px] rounded px-1.5 py-0.5 bg-amber-500/15 text-amber-600 dark:text-amber-400">
                                  {bad.length} item{bad.length === 1 ? '' : 's'} flagged
                                </span>
                              )}
                            </div>
                            {h.what_changed && (
                              <div className="text-xs text-muted-foreground truncate">{h.what_changed}</div>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground text-end whitespace-nowrap">
                            <div className="font-mono">{stamp(h.inspected_at)}</div>
                            <div>{h.inspected_by_name ?? ''}</div>
                          </div>
                        </button>
                        {open && (
                          <div className="px-4 pb-3 space-y-2">
                            {h.odometer_km && (
                              <div className="text-xs text-muted-foreground">
                                Odometer at inspection: <span className="font-mono">{n0(h.odometer_km)} km</span>
                              </div>
                            )}
                            {h.notes && <div className="text-xs">{h.notes}</div>}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                              {(h.items ?? []).map((i) => (
                                <div key={i.key} className="flex items-center justify-between gap-2 text-xs border-b border-border/50 py-1">
                                  <span className="truncate">{i.label ?? i.key}</span>
                                  <span className={
                                    i.state === 'fail' ? 'text-rose-600 dark:text-rose-400'
                                    : i.state === 'attention' ? 'text-amber-600 dark:text-amber-400'
                                    : 'text-muted-foreground'}>{i.state}</span>
                                </div>
                              ))}
                            </div>
                            {(h.items ?? []).length === 0 && (
                              <div className="text-xs text-muted-foreground">No checklist was recorded with this inspection.</div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            <p className="text-xs text-muted-foreground">
              Fuel is {n0(data.fuel.liters)} L across {data.fuel.fills} fill(s) — {data.fuel.source}.
            </p>
          </>
        )}
      </main>
    </div>
  );
}
